import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLines, Reveal, Chapter } from "@/components/Reveal";
import { InflectionCurve } from "@/components/Curve";
import { Marquee } from "@/components/Marquee";
import { Differentiator } from "@/components/Differentiator";
import { CTASection } from "@/components/CTASection";
import { BookCallLink } from "@/components/BookCall";

const HERO_IMG =
  "https://images.unsplash.com/photo-1785057687746-9669fde478e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxleGVjdXRpdmUlMjBtZWV0aW5nJTIwY2luZW1hdGljJTIwZGFya3xlbnwwfHx8fDE3ODc0OTc0NjZ8MA&ixlib=rb-4.1.0&q=85";
const DISCUSSION_IMG =
  "https://images.pexels.com/photos/8371735/pexels-photo-8371735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SERVICES = [
  {
    n: "01",
    title: "Executive Coaching",
    desc: "Clarity and capability for CEOs, executives, and high-potential leaders.",
  },
  {
    n: "02",
    title: "Fractional COO",
    desc: "Hands-on operating leadership for scaling companies.",
  },
  {
    n: "03",
    title: "PMO Development & Transformation",
    desc: "Governance and delivery capability that works.",
  },
  {
    n: "04",
    title: "AI Transformation Advisory",
    desc: "Strategy, workflow redesign, and the people-side of AI adoption.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img
            src={HERO_IMG}
            alt=""
            className="h-[120%] w-full object-cover opacity-40"
            style={{ filter: "saturate(0.4) brightness(0.7)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/60 to-navy" />
        </motion.div>

        <motion.div
          className="relative mx-auto w-full max-w-7xl px-6 pt-32 pb-24 md:px-10"
          style={{ y: contentY, opacity: fade }}
        >
          <motion.p
            className="overline-label mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
            data-testid="hero-overline"
          >
            QuInflection Advisory
          </motion.p>

          <h1
            className="font-serif text-5xl leading-[0.95] tracking-tighter text-white sm:text-6xl md:text-7xl lg:text-8xl"
            data-testid="hero-headline"
          >
            <MaskedLines
              lines={[
                "Own Your",
                <span key="tp" className="italic text-teal">Turning Point</span>,
              ]}
            />
          </h1>

          <motion.p
            className="mt-10 max-w-xl font-sans text-base leading-relaxed text-white/70 md:text-lg"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-subheadline"
          >
            Executive coaching, operational leadership, and AI-enabled transformation —
            for CEOs ready to scale, evolve, and lead their people through change.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <BookCallLink testid="hero-book-call" className="btn-primary">
              Book a Call
              <ArrowUpRight className="h-4 w-4" />
            </BookCallLink>
            <Link to="/services" data-testid="hero-see-how" className="btn-ghost">
              See How We Work
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          className="absolute bottom-8 left-6 md:left-10"
          style={{ opacity: fade }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
        >
          <div className="flex items-center gap-3 text-white/40">
            <ArrowDown className="h-4 w-4 animate-bounce" />
            <span className="font-sans text-xs uppercase tracking-[0.25em]">Scroll</span>
          </div>
        </motion.div>
      </section>

      {/* 01 // WHO WE ARE */}
      <section className="relative" data-testid="intro-section">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <Chapter index="01" label="Who We Are" className="md:sticky md:top-28" />
            </div>
            <div className="md:col-span-8">
              <Reveal>
                <p className="font-serif text-2xl leading-snug text-white sm:text-3xl">
                  QuInflection Advisory helps organizations and leaders successfully navigate
                  their most critical turning points.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-2xl font-sans text-base leading-relaxed text-white/60 md:text-lg">
                  We combine executive coaching, strategic operational leadership, and hands-on
                  project execution to help CEOs scale their businesses, adopt AI across their
                  workflows and systems, and build the leadership teams and workforce strategies
                  that make transformation stick.
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="mt-6 max-w-2xl font-sans text-base leading-relaxed text-white/60 md:text-lg">
                  Unlike traditional consultants who hand over a report and move on, QuInflection
                  partners with your organization to build lasting capability — not just
                  recommendations.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 02 // WHY NOW */}
      <section className="relative border-t border-white/10 bg-navy-deep" data-testid="why-now-section">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <Chapter index="02" label="Why Now" />
          <div className="mt-14 grid items-center gap-16 md:grid-cols-12">
            <div className="md:col-span-7">
              <Reveal>
                <h2 className="font-serif text-3xl leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
                  AI Is Changing How Companies Work.{" "}
                  <span className="italic text-teal">We Help You Change With It.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-xl font-sans text-base leading-relaxed text-white/60 md:text-lg">
                  Adopting AI isn't a technology project — it's a people project. New workflows,
                  new tools, new roles, and new expectations of your teams. QuInflection guides
                  organizations through that shift end-to-end: from AI strategy and implementation,
                  to the change management and staffing decisions that determine whether it
                  actually takes hold.
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <Link to="/services" data-testid="why-now-link" className="btn-ghost mt-10">
                  Explore AI Transformation
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
            <div className="md:col-span-5">
              <Reveal delay={0.2}>
                <motion.div
                  className="overflow-hidden"
                  initial={{ clipPath: "inset(0 100% 0 0)" }}
                  whileInView={{ clipPath: "inset(0 0% 0 0)" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                >
                  <img
                    src={DISCUSSION_IMG}
                    alt="Two leaders in a working session"
                    className="aspect-[4/5] w-full object-cover"
                    style={{ filter: "saturate(0.5) brightness(0.85) hue-rotate(-8deg)" }}
                    data-testid="why-now-image"
                  />
                </motion.div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 03 // WHAT WE DO */}
      <section className="border-t border-white/10" data-testid="services-preview-section">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <div>
              <Chapter index="03" label="What We Do" />
              <Reveal delay={0.1}>
                <h2 className="mt-8 font-serif text-3xl tracking-tight text-white sm:text-4xl md:text-5xl">
                  One Partner. Every Dimension.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/services"
                data-testid="services-preview-all"
                className="group flex items-center gap-2 font-sans text-sm tracking-wide text-white/60 transition-colors duration-300 hover:text-teal"
              >
                All services
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-16 divide-y divide-white/10 border-y border-white/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08} y={20}>
                <Link
                  to="/services"
                  data-testid={`service-preview-${s.n}`}
                  className="group grid items-baseline gap-4 py-8 transition-colors duration-300 md:grid-cols-12 md:py-10"
                >
                  <span className="font-sans text-xs tracking-[0.3em] text-teal md:col-span-2">
                    {s.n}
                  </span>
                  <h3 className="font-serif text-2xl text-white transition-transform duration-300 group-hover:translate-x-2 sm:text-3xl md:col-span-6">
                    {s.title}
                  </h3>
                  <p className="font-sans text-sm leading-relaxed text-white/50 md:col-span-3">
                    {s.desc}
                  </p>
                  <span className="hidden justify-self-end md:col-span-1 md:block">
                    <ArrowUpRight className="h-6 w-6 text-white/30 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-teal" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 04 // THE DIFFERENCE */}
      <div className="border-t border-white/10 bg-navy-deep">
        <div className="mx-auto max-w-7xl px-6 pt-24 md:px-10">
          <Chapter index="04" label="The Difference" />
        </div>
        <Differentiator />
      </div>

      {/* TRUST LINE */}
      <section className="border-t border-white/10" data-testid="trust-section">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10">
          <Reveal>
            <p className="max-w-3xl font-serif text-xl italic leading-relaxed text-white/50 sm:text-2xl">
              "Trusted by scaling technology, professional services, engineering, and defence
              companies across Canada."
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <CTASection
        testid="home-cta"
        headline="Ready to Navigate Your Turning Point?"
        body="Whether you're scaling operations, adopting AI, or developing your leadership team, let's talk about where you are — and where you need to go."
      />
    </main>
  );
}
